import type { PropsWithoutRef, DetailedHTMLProps, InputHTMLAttributes } from 'react';
import { forwardRef } from 'react';
import styled from 'styled-components';

const CheckboxContainer = styled.div``;

const CheckboxLabel = styled.label<{ $color?: string; $fontSize?: string; $hasError: boolean }>`
  display: inline-flex;
  align-items: flex-start;
  font-family: ShellBook;
  color: ${({ theme, $color, $hasError }) =>
    $hasError ? theme.palette.accent.secondary.strong : $color ?? theme.palette.text.onSurface.strong};
  font-size: ${({ $fontSize }) => $fontSize ?? 'inherit'};
  user-select: none;
  > input {
    position: absolute;
    height: 0;
    width: 0;
    opacity: 0;
  }

  > input + div {
    height: 20px;
    min-width: 20px;
    box-sizing: border-box;
    border-radius: 2px;
    position: relative;
    margin-right: ${({ theme }) => theme.spacing(2)};
    border: 2px solid
      ${({ theme, $hasError }) =>
        $hasError ? theme.palette.accent.secondary.strong : theme.palette.text.onSurface.strong};

    &::after {
      content: '';
      position: absolute;
      display: none;
      left: 5px;
      width: 5px;
      height: 10px;
      border: solid black;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
  }

  > input:checked + div {
    background-color: ${({ theme }) => theme.palette.focus.strong} !important;
    border-color: ${({ theme }) => theme.palette.focus.strong} !important;

    &::after {
      display: block;
      border-color: white;
    }
  }

  &:hover {
    > input + div {
      background-color: ${({ theme }) => theme.palette.focus.subtle};
      border-color: ${({ theme }) => theme.palette.focus.strong};
    }
    > input:disabled + div {
      background-color: transparent !important;
    }
  }

  > input:disabled + div {
    border-color: ${({ theme }) => theme.palette.text.onSurface.subtle};
    cursor: not-allowed;
  }

  > input:disabled + div + div {
    color: ${({ theme }) => theme.palette.text.onSurface.subtle};
    cursor: not-allowed;
  }

  > input:checked:disabled + div {
    background-color: ${({ theme }) => theme.palette.text.onSurface.inactive} !important;
    border-color: ${({ theme }) => theme.palette.text.onSurface.inactive} !important;
    cursor: not-allowed;
  }
  &:focus-within {
    outline: 1px dashed ${({ theme }) => theme.palette.text.onSurface.subtle};
  }

  @media print {
    > input {
      position: static;
      opacity: 1;
      height: 20px;
      min-width: 20px;
    }

    > input + div {
      display: none;
    }
  }
`;

const ErrorText = styled.div`
  color: ${({ theme }) => theme.palette.accent.secondary.strong};
  &::before {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.palette.accent.secondary.strong};
    color: white;
    margin-right: ${({ theme }) => theme.spacing(3)};
    content: '!';
    width: 18px;
    height: 18px;
    margin-left: 2px;
  }
`;

export type CheckboxProps = PropsWithoutRef<
  DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>
> & {
  label?: string;
  error?: string | boolean;
  labelColor?: string;
};

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>((props, ref) => {
  const { label, error, className, labelColor, ...inputProps } = props;
  return (
    <CheckboxContainer className={className}>
      <CheckboxLabel data-testid={`checkbox-label-${props.name}`} $color={labelColor} $hasError={Boolean(error)}>
        <input type="checkbox" {...inputProps} ref={ref} aria-label={label} />
        <div />
        <div>{label}</div>
      </CheckboxLabel>
      {typeof error === 'string' && error && <ErrorText>{error}</ErrorText>}
    </CheckboxContainer>
  );
});

Checkbox.displayName = 'Checkbox';
